import { FC, useState } from 'react'

import {
  Box,
  Button,
  Icon,
  MediumIcon,
  SearchField,
  Text,
} from '@factorialco/deprecated-design-system'

import ScrollableList from 'modules/core/components/ScrollableList'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'

import * as styles from './index.vanilla.css'

export interface IItem {
  id: number
  name: string
  level: number
  parentId?: number | null
  children?: IItem[]
}

interface Props {
  items: IItem[]
  title: string
  selectedItem?: IItem
  isDirty: boolean
  titleIcon?: MediumIcon
  confirmSelection: (item?: IItem) => void
  setSelectedItem: (value?: IItem) => void
  noSelectionTitle?: string
}

export const NavigationSelector: FC<Props> = ({
  items,
  title,
  isDirty,
  titleIcon,
  selectedItem,
  setSelectedItem,
  confirmSelection,
  noSelectionTitle,
}) => {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const [selectedItemParent, setSelectedItemParent] = useState<IItem | undefined>()
  const [currentItems, setCurrentItems] = useState<IItem[]>(items)
  const currentLevel = currentItems[0]?.level || 1
  const isParent = currentLevel === 1
  const hasGrandParent = currentLevel > 2

  const buildFlattenItems = (value: IItem[]): IItem[] => {
    return value.reduce<IItem[]>((acc, item) => {
      acc.push(item)
      if (item.children) {
        acc.push(...buildFlattenItems(item.children))
      }
      return acc
    }, [])
  }
  const flattenItems = useLazyRef(() => buildFlattenItems(items)).current

  useEffect(() => {
    if (selectedItem) {
      setCurrentItems(
        flattenItems.filter(
          (item) => item.level === selectedItem.level && item.parentId === selectedItem.parentId
        ) || []
      )
      setSelectedItemParent(
        flattenItems.find((item) => item.id === selectedItem.parentId) || undefined
      )
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const itemsToDisplay = useUnsafeMemo(() => {
    return currentItems.filter((item) =>
      item.name.toLowerCase().includes(debouncedSearch.toLowerCase())
    )
  }, [debouncedSearch, currentItems]) // eslint-disable-line react-hooks/exhaustive-deps

  const goBack = () => {
    if (currentLevel === 1) return

    setSelectedItemParent(flattenItems.find((item) => item.id === selectedItemParent?.parentId))

    const newItems = flattenItems.filter((item) => item.level === currentLevel - 1)
    setCurrentItems(newItems)
  }

  const gotoChildren = (children: IItem[], parentItem: IItem) => {
    setSelectedItemParent(parentItem)
    setCurrentItems(children)
  }

  const currentTitle = selectedItemParent?.name || title
  const grantParentItem = flattenItems.find((item) => item.id === selectedItemParent?.parentId)

  const backButtonText = hasGrandParent
    ? grantParentItem?.name
    : isParent
    ? i18n.t('common.back')
    : title

  return (
    <Box>
      <Box
        paddingX="s16"
        paddingY="s8"
        gap="s12"
        background="grey100"
        border={{
          bottom: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Button
          onClick={() => goBack()}
          type="secondary"
          disabled={isParent}
          iconName="chevron-left"
        >
          {backButtonText}
        </Button>
        <Box flexGrow>
          <SearchField
            variant="big"
            search={search}
            onSearch={onSearch}
            placeholder={i18n.t('common.search')}
          />
        </Box>
        <Box flexDirection="row" gap="s4" alignItems="center">
          {titleIcon && <Icon.Medium icon={titleIcon} />}
          <Text size="200">{currentTitle}</Text>
        </Box>
      </Box>
      <Box gap="s12">
        <ScrollableList size="small">
          <Box component="ul">
            {isParent && noSelectionTitle && (
              <Box
                cursor="pointer"
                onClick={() => confirmSelection()}
                paddingY="s12"
                paddingX="s16"
                width="full"
                textAlign="left"
              >
                <Text size="200" ellipsis>
                  {noSelectionTitle}
                </Text>
              </Box>
            )}
            {itemsToDisplay.map((item) => {
              const isChildSelected = selectedItem
                ? selectedItem?.level > item.level && selectedItem.parentId === item.id
                : false
              const selected =
                (selectedItem?.id === item.id && selectedItem.level === item.level) ||
                isChildSelected

              const { children: allowedChildren } = item

              return (
                <Box
                  component="li"
                  className={styles.treeSelector}
                  background={selected ? 'viridian100' : undefined}
                  key={item.id}
                  flexDirection="row"
                  alignItems="center"
                >
                  <Box
                    cursor="pointer"
                    onClick={() => setSelectedItem(selected ? undefined : item)}
                    paddingY="s12"
                    paddingX="s16"
                    width="full"
                    textAlign="left"
                  >
                    <Text color={selected ? 'viridian1200' : undefined} size="200" ellipsis>
                      {item.name}
                    </Text>
                  </Box>
                  {!!allowedChildren?.length && (
                    <Box
                      paddingX="s16"
                      paddingY="s6"
                      cursor="pointer"
                      onClick={() => gotoChildren(allowedChildren, item)}
                    >
                      <Box className="arrow" borderRadius={{ all: 'abs100' }}>
                        <Icon.Medium icon="chevron-right" color="grey600" />
                      </Box>
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>
        </ScrollableList>
      </Box>
      <button
        disabled={!isDirty}
        className={styles.confirmFilter}
        onClick={() => confirmSelection(selectedItem)}
      >
        <Text.Normal size="300" weight="medium" color={isDirty ? 'grey800' : 'grey600'}>
          {i18n.t('common.select')}
        </Text.Normal>
      </button>
    </Box>
  )
}
